// League standings. One list, not two.
//
// This page used to render every manager twice: a condensed leaderboard, then
// a stack of large cards repeating the same names with a roster grid under
// each. Most of the second block was dead. It read member.lineup,
// member.freezes, member.freeAgentMoves and member.duplicateWeek1, none of
// which exist on league_members, so every roster rendered empty and every
// badge was invisible. Rosters are covered properly by the Scoreboard tab,
// which reads weekly_lineups, so this page is now purely the ranking.
//
// Live captain / 5x / weekly points come from weekly_lineups via
// useLeagueData, not from the member row.
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Zap, Snowflake, MessageCircle, ChevronUp, ChevronDown, ChevronsLeft, ChevronsRight, Crown } from "lucide-react";
import ScoringSystemModal from "../components/ScoringSystemModal";
import LoadingScreen from "../components/LoadingScreen";
import { useLeague } from "../context/LeagueContext";
import { useTeams } from "../hooks/useTeams";
import { useSeasonConfig } from "../hooks/useSeasonConfig";
import { useLeagueRecords } from "../hooks/useLeagueRecords";
import { useFreeAgentMoves } from "../hooks/useFreeAgentMoves";
import { isOffSeason, PLAYOFF_BYES, PLAYOFF_TEAMS } from "../utils/season";
import { getCurrentWeekNumber, getStartersPlayedCount, getDisplayData } from "../utils/leagueUtils";
import { STAT_TEXT_STYLE } from "../utils/statText";
import { getScheduleEntry } from "../utils/scheduleUtils";
import { normalizeTeamName, teamLabel } from "../utils/teamName";
import TeamLogoImage from "../components/league/TeamLogoImage";
import TeamDrawer from "../components/league/TeamDrawer";
import { useLeagueData } from "../hooks/useLeagueData";
import UserAvatar from "../components/league/UserAvatar";
import ReportBlockModal from "../components/ReportBlockModal";
import WeekSelector from "../components/league/WeekSelector";
import HallOfFame from "./HallOfFame";
import ShareButton from "../components/ShareButton";
import HelpMark from "../components/HelpMark";
import TourAnchor from "../tours/TourAnchor";
import { buildStandingsShareText } from "../utils/standingsShare";


// Smack talk is capped at 80 characters (migration 040), which at this width
// and size is at most four lines, so the bubble never needs to scroll.
const BUBBLE_WIDTH = 224;
// Dec% and Capt% share the Wk/Total columns' width scale rather than each
// picking their own: a percentage needs less room than a raw point total,
// but a mismatched width would misalign the header labels above them.
const STAT_COL_WIDTH = 46;
// "Capt Pts" is the one heading too long for a stat column, and it has to
// fit with the sort arrow beside it: at 58 the arrow pushed "Pts" onto a
// second line, which made this header taller than the identity column's and
// put every row out of line with its stats.
const CAPT_PTS_COL_WIDTH = 70;
const TOTAL_COL_WIDTH = 44;
const WK_COL_WIDTH = 38;
// Rank, avatar and name. No longer a fixed number: the identity column now
// flex-grows into whatever room STATS_VISIBLE_WIDTH below doesn't reserve,
// so a wider phone hands the extra space to the name instead of leaving it
// blank. This is only the floor it won't shrink past.
const IDENTITY_MIN_WIDTH = 150;
// The identity column folded down to rank and avatar, which hands the rest
// of the row to the stat columns. The row's left padding, the rank, the gap,
// the 40px avatar, and room on the right for the avatar's corner badge.
const IDENTITY_COLLAPSED_WIDTH = 12 + 16 + 4 + 40 + 10;
// The stats column's *visible* width, before any scrolling: exactly enough
// for Total and Wk, on every device. This has to be a fixed number, not
// "whatever room is left" (that was the previous approach): flex-1 on this
// column meant a wide enough phone had room to show Dec%/Capt% without any
// scrolling at all, so which columns appeared by default silently depended
// on screen size. A fixed width makes Total/Wk-only the default everywhere,
// and Dec%/Capt% reachable by scroll everywhere, with no exceptions to
// depend on. Row's own left padding (px-3) plus Total, the gap-2 between
// columns, and Wk.
const STATS_VISIBLE_WIDTH = 12 + TOTAL_COL_WIDTH + 8 + WK_COL_WIDTH;
// Every row's height, identity side and stats side alike, clamped with
// overflow: hidden rather than just floored with minHeight. The two sides
// are independently-stacked columns (see the comment on renderIdentityRow),
// so nothing keeps them the same height on their own, and a minHeight floor
// only holds if neither side's content ever needs more room than that: a
// long team name's chip line, or an emoji in it, can legitimately want more
// on one device's fonts than another's, and whichever row that happens to
// hit goes taller on the identity side than its stats counterpart, throwing
// every row below it out of alignment. Clamping both sides to the same
// height and hiding the overflow makes that impossible regardless of
// content or device: identity's 40px avatar plus its py-2 padding sets the
// number (40 + 8 + 8), and the stats side is held to it whether or not it
// would have needed the room.
const ROW_HEIGHT = 56;
// A row with the filled "5X" (in play) pill needs more vertical room than the pill
// alone would fit inside ROW_HEIGHT, so this is the one case that gets its
// own taller clamp instead of the shared constant, on both sides for the
// same reason ROW_HEIGHT is clamped at all: identity and stats must agree.
const ROW_HEIGHT_TRIP_PLAY = 64;
// Same clamp, same reasoning, for the "Playoff line" divider: the real one
// on the identity side and its invisible same-height spacer on the stats
// side (see renderPlayoffLine) must match exactly or every row after the
// cut line drifts out of sync.
const DIVIDER_HEIGHT = 40;
// The expanded starters panel. Fixed for the same reason as ROW_HEIGHT: the
// panel sits in the identity column and a blank spacer of this exact height
// sits in the stats column, so the rows below stay aligned.
// One row of five columns: logo, a name allowed two lines, and the status.
const EXPANDED_HEIGHT = 104;
// How much of the table a tour step lights up: the header row and the top two
// managers. See TourAnchor.
const STANDINGS_TOUR_HEIGHT = 27 + 2 * ROW_HEIGHT;
const STARTER_SLOTS = 5;

// ESPN's WIN% column formatting: a three-decimal ratio with the leading
// zero dropped (".724", not "0.724"), since the header already carries the
// "%" and a second one on every row would be noise. A perfect record keeps
// its leading digit ("1.000"), same as ESPN's own column.
function formatRatio(correct, total) {
  if (!total) return null;
  const fraction = correct / total;
  const fixed = fraction.toFixed(3);
  return fraction < 1 ? fixed.replace(/^0/, "") : fixed;
}
// Roughly the tallest the bubble gets. Only used to decide whether it opens
// downwards or flips above the button.
const BUBBLE_MAX_HEIGHT = 96;

function MyLeague() {
  const { leagueId } = useParams();
  const { leagueData: ctxLeagueData, currentWeek, currentMemberId, blockedIds, refreshLeague } = useLeague();
  const { teams: allTeams } = useTeams();
  const { config: seasonConfig } = useSeasonConfig();
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [viewMode, setViewMode] = useState("current"); // 'current' vs 'historical'
  // { memberId, text, rect } of the bubble currently open, or null. rect is
  // captured from the icon's own click event so the bubble can be positioned
  // with `fixed` (the standings card is `overflow-hidden` for its rounded
  // corners, so an absolutely-positioned bubble would get clipped instead of
  // floating above the row).
  const [smackTalkBubble, setSmackTalkBubble] = useState(null);
  // Tapping a row avatar opens it larger. The image is already in the DOM at
  // list size, so this costs no extra request.
  const [zoomedMember, setZoomedMember] = useState(null);
  // The manager whose report/block sheet is open. Reached from their card, so
  // the control sits on the person it acts on rather than in a settings page.
  const [reportTarget, setReportTarget] = useState(null);
  // Which stat column the table is sorted by, beyond the default standings
  // order. null means "the actual standings" (season points, the same order
  // the rank badge, its movement arrow and the playoff line all assume).
  // Tapping Dec% or Capt% re-orders the rows by that stat without disturbing
  // what "rank" means: the badge keeps showing each manager's real standing
  // (via rankByMemberId below), it just stops being the row's own position
  // once the rows are in some other order.
  const [statSort, setStatSort] = useState({ key: null, dir: "desc" });
  // The one manager whose starters are showing, or null. A single id rather
  // than a set, so opening a row closes whichever was open before it.
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  // Names, chips and starter dots folded away, leaving rank and avatar, so
  // the stat columns get the width instead of two of seven showing. Not
  // remembered between visits: the full row is what the page tour points at
  // and what a manager expects to land on.
  const [namesCollapsed, setNamesCollapsed] = useState(false);
  // School name of the team open in the drawer, reached from a starters panel.
  const [drawerTeam, setDrawerTeam] = useState(null);

  useEffect(() => {
    if (!smackTalkBubble) return undefined;
    const close = (e) => {
      if (!e.target.closest("[data-smack-talk-bubble]")) setSmackTalkBubble(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [smackTalkBubble]);

  useEffect(() => {
    if (!zoomedMember) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setZoomedMember(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zoomedMember]);

  const {
    members,
    loading,
    scheduleData,
    weeklyStandings,
    availableWeeks,
    standingsLoading,
    loadWeeklyStandings,
  } = useLeagueData({ leagueId, currentWeek, ctxLeagueData, blockedIds });
  const { decisionAccuracy, captainAccuracy, benchPoints, captainPoints, weekRange } = useLeagueRecords();
  const { movesByMemberId } = useFreeAgentMoves(leagueId);

  if (loading) {
    // No min-h-screen here. LeagueTab already sets one, and nesting a second
    // pushed the spinner a full screen down, so the page looked blank while
    // it was loading.
    return <LoadingScreen message="Loading league standings..." fullScreen={false} />;
  }

  const displayData = getDisplayData({ viewMode, members, weeklyStandings, selectedWeek });
  const currentWeekNum = getCurrentWeekNumber(currentWeek);
  const isCurrent = viewMode === "current";

  // Each manager's real standing, independent of whatever the rows are
  // currently sorted by. In "current" view this is just position in the
  // points-sorted list (displayData's own order before a stat sort touches
  // it); in "historical" view weeklyStandings already stamps a rank on every
  // row. Built once, up front, for both view modes, so every other lookup
  // below (the sort getter, the row view models) reads one map instead of
  // re-deriving the same branch twice.
  const rankByMemberId = new Map();
  displayData.forEach((m, i) => rankByMemberId.set(m.id, viewMode === "historical" ? m.rank ?? i + 1 : i + 1));

  // The table as share text: each manager's real standing and season total,
  // whatever column the rows happen to be sorted by on screen. A past week
  // says so, since its totals are as they stood then.
  const getStandingsShareText = () => buildStandingsShareText({
    leagueName: ctxLeagueData?.name || "League",
    weekLabel: !isCurrent ? `After Week ${selectedWeek}` : currentWeekNum >= 1 ? `Week ${currentWeekNum}` : null,
    // Only on the live table, which is the only one the page draws it on.
    playoffSpots: isCurrent ? PLAYOFF_TEAMS : null,
    rows: displayData.map((m) => ({
      rank: rankByMemberId.get(m.id),
      firstName: m.firstName,
      teamName: m.teamName,
      points: m.points ?? 0,
    })),
  });

  const decisionByMemberId = new Map(decisionAccuracy.map((r) => [r.memberId, r]));
  const captainByMemberId = new Map(captainAccuracy.map((r) => [r.memberId, r]));
  // Season totals, the same ones the Hall of Fame's Captain Whisperer and the
  // Hall of Shame's bench record rank.
  const captainPtsByMemberId = new Map(captainPoints.map((r) => [r.memberId, r]));
  const benchByMemberId = new Map(benchPoints.map((r) => [r.memberId, r.value]));
  // Each manager's best and worst finished week.
  const weekRangeByMemberId = new Map(weekRange.map((r) => [r.memberId, r]));

  // One getter per sortable column. Rank is stored negated so "desc" (every
  // column's first-click direction) still means "best at the top" the same
  // way it does for the other three: a lower rank number is better, the
  // opposite polarity from points/percentages, so flipping the sign here is
  // what keeps the toggle behaving the same regardless of which header was
  // tapped. FA has no "better" direction, just an amount, so it sorts like
  // Total and Wk: most active manager first on the default tap.
  const STAT_GETTERS = {
    rank: (m) => -(rankByMemberId.get(m.id) ?? Infinity),
    total: (m) => m.points ?? null,
    week: (m) => m.weeklyPoints ?? null,
    bestWeek: (m) => weekRangeByMemberId.get(m.id)?.best?.value ?? null,
    worstWeek: (m) => weekRangeByMemberId.get(m.id)?.worst?.value ?? null,
    decision: (m) => decisionByMemberId.get(m.id)?.value ?? null,
    captain: (m) => captainByMemberId.get(m.id)?.value ?? null,
    captainPts: (m) => captainPtsByMemberId.get(m.id)?.value ?? null,
    bench: (m) => benchByMemberId.get(m.id) ?? 0,
    fa: (m) => movesByMemberId.get(m.id) ?? 0,
  };

  const sortedData = statSort.key
    ? [...displayData].sort((a, b) => {
        const getValue = STAT_GETTERS[statSort.key];
        const av = getValue(a);
        const bv = getValue(b);
        // No data yet always sinks to the bottom, in either direction: a
        // manager with no fully-decided week isn't "worst", they're unranked.
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return statSort.dir === "asc" ? av - bv : bv - av;
      })
    : displayData;

  const toggleStatSort = (key) => {
    setStatSort((cur) => (cur.key === key ? { key, dir: cur.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  };

  // Whether sortedData's current order is the real standings order, not just
  // "no sort key set". Sorting by Total or Rank, descending, reproduces that
  // same order (best first) even though a key is active, so the playoff line
  // and the movement-free rank badge logic both need this rather than the
  // narrower "!statSort.key" check that only covered the unsorted case.
  const isStandingsOrder =
    !statSort.key || ((statSort.key === "total" || statSort.key === "rank") && statSort.dir === "desc");

  // getCurrentWeekNumber floors "Preseason" and "Off-Season" to 1, so anything
  // derived from it alone claimed week 1 was underway before a game had been
  // played. isOffSeason reads config.currentWeek directly, ahead of that.
  const isPreseason = isOffSeason(seasonConfig?.currentWeek);

  // Which of three stages the week is in, read from the games themselves
  // rather than from the season config. The config knows which week it is; it
  // does not know whether that week's games have kicked off, and that is the
  // question a standings reader actually has: are these numbers still moving?
  //
  // The preseason needs no case of its own. Nothing has kicked off then
  // either, so it lands on "not started" naturally.
  const weekGames = Object.values(scheduleData || {});
  const allComplete = weekGames.length > 0 && weekGames.every((g) => g.gameComplete);
  // hasLiveGame (useLeagueData) already accounts for a mid-game delay, which
  // reports the same 'delayed' status as a delay before kickoff. Checking
  // gameStatus directly here used to miss that and drop the week selector's
  // live pulse the moment a live game got delayed.
  const anyUnderway = weekGames.some((g) => g.gameComplete || g.hasLiveGame);

  let stage = "upcoming";
  if (!isCurrent || allComplete) stage = "final";
  else if (anyUnderway && !isPreseason) stage = "live";

  // Six in every league size. See PLAYOFF_TEAMS: three playoff weeks only fit
  // a six-team bracket, so this no longer varies with maxManagers.
  const playoffSpots = PLAYOFF_TEAMS;

  // Every row needs the same handful of derived values whether it ends up
  // rendered on the frozen (identity) side or the scrolling (stats) side, so
  // they're computed once per member here rather than twice.
  // Past weeks render weekly_standings rows, which carry no lineup or block
  // flag, so the panel reads both off the live member record by id.
  const membersById = new Map(members.map((m) => [m.id, m]));

  const rowViewModels = sortedData.map((member, idx) => {
    // Real standing, not row position: once a stat sort is active the rows
    // are no longer in standings order, but the badge still needs to answer
    // "what place are they actually in".
    const rank = rankByMemberId.get(member.id) ?? idx + 1;
    const decisionEntry = decisionByMemberId.get(member.id) ?? null;
    const captainEntry = captainByMemberId.get(member.id) ?? null;
    // Unlike Dec%/Capt%, 0 here is a real, always-known answer ("made no
    // moves"), not "not decided yet", so it renders as a number rather than
    // the dash those columns fall back to.
    const faMoves = movesByMemberId.get(member.id) ?? 0;
    // No captained week yet is a dash, like Dec% and Capt%. Bench is always a
    // number: nothing left on the bench is a real zero.
    const captainPtsEntry = captainPtsByMemberId.get(member.id) ?? null;
    const benchPts = benchByMemberId.get(member.id) ?? 0;
    // Null until the manager has a finished week.
    const weekRangeEntry = weekRangeByMemberId.get(member.id) ?? null;
    // "Still has it", not "is using it this week". league_members.has_trip_play
    // is granted when the draft completes (migration 038) and cleared once the
    // 5x is spent, so it answers the question a standings row should: what
    // does this manager still have in hand.
    const hasTripPlayLeft = Boolean(member.hasTripPlay) && isCurrent;
    // "Is using it this week", the other half of that question. Mutually
    // exclusive with hasTripPlayLeft above: has_trip_play is cleared the same
    // write that sets trip_play_team, so a manager is never both still
    // holding it and actively playing it.
    const hasTripPlayInPlay = Boolean(member.tripPlayTeam) && isCurrent;
    const freezes = member.freezesRemaining ?? 0;
    const { complete: startersComplete, inProgress: startersInProgress, total: startersTotal } =
      getStartersPlayedCount(member, { viewMode, scheduleData, allTeams });
    // currentMemberId is league_members.id, which is what member.id holds.
    const isMe = currentMemberId != null && member.id === currentMemberId;
    const rowBg = isMe ? "var(--color-band-bg)" : idx % 2 === 1 ? "var(--color-bg-row-alt)" : "var(--color-bg-card)";
    // Reverts to ROW_HEIGHT on its own the moment hasTripPlayInPlay goes
    // false, whether that's the manager removing the play mid-week or the
    // week simply rolling over: both clear member.tripPlayTeam for isCurrent,
    // which is the only thing this reads.
    const rowHeight = hasTripPlayInPlay ? ROW_HEIGHT_TRIP_PLAY : ROW_HEIGHT;
    return {
      member, idx, rank, decisionEntry, captainEntry, captainPtsEntry, benchPts, weekRangeEntry, faMoves, hasTripPlayLeft, hasTripPlayInPlay,
      freezes, startersComplete, startersInProgress, startersTotal, rowBg, rowHeight,
      // Only meaningful in standings order, so it sits out any other sort
      // rather than pointing at whichever manager happens to land at index
      // playoffSpots.
      showPlayoffLine: isCurrent && isStandingsOrder && idx === playoffSpots,
      // Follows the real rank rather than the row, so it holds under a stat
      // sort too. Current view only: a past week's snapshot seeds nothing.
      hasBye: isCurrent && rank <= PLAYOFF_BYES,
      // Never while the names are folded: the panel is sized against the
      // fixed stats width, and its chevron is folded away with the names.
      expanded: !namesCollapsed && !membersById.get(member.id)?.blocked && expandedMemberId === member.id,
    };
  });

  // The playoff-line divider. Both sides render the same lines, at the same
  // height, so the row break itself is visible on the stats side too: an
  // invisible gap there used to read as a misalignment bug rather than an
  // intentional skip, since nothing on that side explained why the numbers
  // had stepped away from their names. Only the "Playoff line" pill itself
  // is identity-only, since repeating that label on the stats side would be
  // redundant next to a line that already says the same thing.
  //
  // identitySide says which column is asking. The label goes wherever there
  // is room for it: the identity column normally, the stats column when the
  // names are folded and the identity column is too narrow to hold the pill
  // at all, in which case that side draws the bare line.
  const renderPlayoffLine = (identitySide) => {
    const showLabel = identitySide ? !namesCollapsed : namesCollapsed;
    return (
      <div
        className="flex items-center gap-3 py-2 px-3"
        style={{ height: DIVIDER_HEIGHT, overflow: "hidden" }}
      >
        <div className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
        {!(identitySide && namesCollapsed) && (
          <span
            className={`flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full whitespace-nowrap ${showLabel ? "" : "invisible"}`}
            style={{ backgroundColor: "var(--color-danger-bg)", color: "var(--color-danger-fill)", border: "1px solid var(--color-danger-border)" }}
          >
            Playoff line
          </span>
        )}
        <div className="flex-1 h-px" style={{ backgroundColor: "var(--color-border)" }} />
      </div>
    );
  };

  // Identity (rank, avatar, name, chips): the frozen left column.
  //
  // This used to be a sticky block sharing one flex row with the stat
  // columns, pinned via position: sticky inside the row's own overflow-x
  // scroll. That's the standard technique, but WebKit's implementation only
  // partially honors it inside a momentum-scrolled (-webkit-overflow-scrolling:
  // touch) container: the sticky element crept along with the scroll instead
  // of staying put, moving less than the rest of the row but still moving.
  // Splitting identity into its own column, entirely outside anything that
  // scrolls, removes the mechanism that could drift: there is no scroll
  // happening near it for it to partially follow.
  const renderIdentityRow = (vm) => {
    const { member, rank, hasBye, hasTripPlayLeft, hasTripPlayInPlay, freezes, startersComplete, startersInProgress, startersTotal, rowBg, rowHeight, showPlayoffLine, expanded } = vm;
    return (
      <div key={member.id}>
        {showPlayoffLine && renderPlayoffLine(true)}
        <div
          data-tour-row=""
          className="relative flex items-center gap-1 py-2"
          style={{ backgroundColor: rowBg, height: rowHeight, overflow: "hidden", transition: "height 0.2s", paddingLeft: 12, paddingRight: namesCollapsed ? 0 : 24 }}
        >
          {/* Bottom right corner of the identity column, clear of the name
              line, which truncates before it. */}
          {!namesCollapsed && !membersById.get(member.id)?.blocked && (
            <button
              type="button"
              onClick={() => setExpandedMemberId((cur) => (cur === member.id ? null : member.id))}
              aria-expanded={expanded}
              aria-label={`${expanded ? "Hide" : "Show"} ${member.teamName || "this manager"}'s starters`}
              className="absolute right-0 bottom-0 flex items-end justify-end"
              style={{ width: 36, height: 32, padding: "0 6px 4px 0", color: expanded ? "var(--color-brand)" : "var(--color-text-muted)" }}
            >
              <ChevronDown
                size={16}
                style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
              />
            </button>
          )}
          {/* Plain number, no medal tint or box: ESPN's own standings read
              the rank as text, not a badge, which is what keeps the row's
              weight on the team rather than on its position.

              No movement arrow: it used to compare this week's rank against
              last week's *stored* rank (weekly_standings, written by
              backend/season.js's snapshot on the outgoing week), but that
              snapshot sorts on points alone with no tiebreak, while the live
              standings above now break ties on best-week and
              best-week-captain points. Two managers tied on points can
              therefore get a different order from each source with no real
              change in play between them, and the arrow had no way to tell
              that apart from an actual swing. */}
          <div className="flex-shrink-0 flex items-center gap-1">
            {/* Seeds 1 and 2 carry BYE under the number: they skip the
                opening round of the playoffs. Tucked under the rank rather
                than among the name chips, where it would crowd the line. */}
            <div className="flex-shrink-0 flex flex-col items-center" style={{ width: 16 }}>
              <span className="text-sm font-bold text-center" style={{ color: "var(--color-text-strong)" }}>
                {rank}
              </span>
              {hasBye && (
                <span
                  aria-label="Playoff bye"
                  style={{ marginTop: 1, fontSize: 9, fontWeight: 900, lineHeight: 1, letterSpacing: "0.02em", color: "var(--color-success-text)" }}
                >
                  BYE
                </span>
              )}
            </div>

            {/* Sized against the two-line name block beside it (team name at
                text-sm plus the manager line at 11px), which is what actually
                sets the row height. 40 fills that space; going past it starts
                pushing the row taller. A plain div, not the zoom button
                itself, since the smack talk badge below is a second,
                sibling button overlaid on the same corner-badge pattern as
                the live dot: two buttons can't nest, so the click target
                and the badges live at this level instead. */}
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setZoomedMember(member)}
                className="block rounded-full transition-transform active:scale-95"
                aria-label={`View ${member.teamName || "this manager"}'s avatar`}
              >
                <UserAvatar member={member} size={40} />
              </button>
              {/* Moved off the name line, which had no room to spare once
                  Dec%/Capt% pushed the row wider: a badge on the avatar's
                  free corner (opposite the live dot) costs the row no
                  extra width at all. */}
              {member.smack_talk && (
                <button
                  type="button"
                  data-smack-talk-bubble
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setSmackTalkBubble((cur) =>
                      cur?.memberId === member.id
                        ? null
                        : {
                            memberId: member.id,
                            text: member.smack_talk,
                            rect,
                            flipAbove:
                              rect.bottom + 6 + BUBBLE_MAX_HEIGHT >
                              window.innerHeight,
                          }
                    );
                  }}
                  className="absolute -top-1 -right-1 flex items-center justify-center rounded-full border-2 border-white"
                  style={{ width: 18, height: 18, backgroundColor: "var(--color-brand)", color: "var(--color-bg-card)" }}
                  aria-label={`${member.teamName || "Manager"}'s smack talk`}
                >
                  <MessageCircle size={10} />
                </button>
              )}
            </div>
          </div>

          {/* Two stacked lines, both running the full width left of the row's
              edge. The team name owns the first line on its own; the manager
              name and the chips share the second. Nothing sits beside the
              name, which is what stops a long team name being truncated
              halfway across the row. Folded away with the column: the
              avatar still opens the manager's name. */}
          <div className="flex-1 min-w-0" style={namesCollapsed ? { display: "none" } : undefined}>
            {/* Team name links to this manager's card in the Scoreboard tab's
                carousel: the standings row says where they rank, the
                scoreboard card says why, and this is the jump between them
                instead of a swipe through the whole league to find one team. */}
            <Link
              to={`/${leagueId}/scoreboard?member=${member.id}${isCurrent ? "" : `&week=${selectedWeek}`}`}
              className="flex items-center gap-1.5 no-underline"
            >
              <span className="text-sm font-semibold leading-tight truncate" style={{ color: "var(--color-text-strong)" }}>
                {member.teamName || "Unnamed Team"}
              </span>
            </Link>

            {/* Name and chips share one flexible group. WK and Total used to
                live in this same row, which pinned them to the second line's
                height; they're now direct siblings of the whole name block
                below, centered against the full row like the rank number is,
                rather than sitting low. The extra marginTop only for the
                filled "5X" pill: it is visibly taller than the plain
                icon+label chips the row otherwise carries, and centering the
                two-line block in the row's own extra height (ROW_HEIGHT_TRIP_PLAY)
                was not the same as giving the pill air from the team name
                sitting right above it. */}
            <div className="flex items-center gap-2" style={{ marginTop: hasTripPlayInPlay ? 4 : 0 }}>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="text-[11px] truncate" style={{ color: "var(--color-text-muted)" }}>
                  {member.firstName || "Unknown"}
                </span>

                {/* Only the two resources a manager still holds. The captain
                    crown and its team logo used to sit here too, but the captain
                    is this week's lineup decision and belongs on the Lineup and
                    Scoreboard tabs, not in a standings row. */}
                {(hasTripPlayLeft || hasTripPlayInPlay || (freezes > 0 && isCurrent)) && (
                <span className="flex items-center gap-2 flex-shrink-0" data-tour="league-chips">
                {hasTripPlayLeft && (
                  <span className="flex items-center gap-0.5 flex-shrink-0" title="5x Play still available">
                    <Zap size={11} style={{ color: "var(--color-tripplay-text)" }} />
                    <span className="text-[10px] font-bold" style={{ color: "var(--color-tripplay-text)" }}>5X</span>
                  </span>
                )}

                {/* "In play" outranks "still holding it": it is spent for the
                    rest of the season the moment it is on the field, so a
                    manager checking the table should be able to spot who is
                    live with it right now, not just who could still use one.
                    A filled pill rather than the plain icon+label the other
                    two chips use, to read as the bigger deal that it is.
                    Labelled "5X" only, not "5X IN PLAY": the long label left
                    the manager name a letter or two beside the freeze chip
                    and the starter pips. The fill is what says "in play",
                    and the "?" on the table header spells it out. */}
                {hasTripPlayInPlay && (
                  <span
                    className="flex items-center gap-0.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: "var(--color-tripplay-text)", padding: "3px 6px" }}
                    title="5x Play in play this week"
                    aria-label="5x Play in play this week"
                  >
                    <Zap size={10} color="var(--color-bg-card)" fill="var(--color-bg-card)" />
                    <span className="text-[10px] font-bold" style={{ color: "var(--color-bg-card)" }}>5X</span>
                  </span>
                )}

                {freezes > 0 && isCurrent && (
                  <span className="flex items-center gap-0.5 flex-shrink-0" title={`${freezes} freezes left`}>
                    <Snowflake size={11} style={{ color: "var(--color-freeze)" }} />
                    <span className="text-[10px] font-bold" style={{ color: "var(--color-freeze)" }}>{freezes}</span>
                  </span>
                )}
                </span>
                )}

                {/* One pip per starter slot: blue once that team's game is
                    final, green and pulsing while it's live, grey while it
                    hasn't kicked off. Out of 5 always, not out of however
                    many slots are filled: an empty slot never lights up.
                    Live gets its own color rather than counting as "played"
                    because WK already carries that team's live, provisional
                    points, so a manager can tell "locked in" from "could
                    still move" at a glance. Green matches the avatar dot and
                    the week selector elsewhere on this page, which is
                    already what live means here. */}
                {isCurrent && (
                  <span
                    data-tour="league-dots"
                    className="flex items-center gap-0.5 flex-shrink-0"
                    title={`${startersComplete} of ${startersTotal} starters final, ${startersInProgress} in progress`}
                  >
                    {Array.from({ length: startersTotal }).map((_, i) => {
                      const isComplete = i < startersComplete;
                      const isLive = !isComplete && i < startersComplete + startersInProgress;
                      return (
                        <span
                          key={i}
                          className={`rounded-sm ${isLive ? "animate-pulse" : ""}`}
                          style={{
                            width: 7.5,
                            height: 7.5,
                            backgroundColor: isComplete ? "var(--color-brand)" : isLive ? "var(--color-success)" : "var(--color-border)",
                          }}
                        />
                      );
                    })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        {renderStartersPanel(vm)}
      </div>
    );
  };

  // A manager's starting five for the week on screen, opened from the chevron.
  //
  // Teams and game status only. Scores and points belong to the Scoreboard
  // tab, which is the live surface; repeating them here would give two
  // screens the same numbers, this one refreshed less often.
  //
  // Five columns across rather than a list, which keeps the panel one short
  // row tall and lines up with the five starter squares on the row above.
  //
  // Lives in the identity column but is widened by STATS_VISIBLE_WIDTH so it
  // spans the whole card, painting over the stats column beside it. The stats
  // column renders a blank spacer of the same animated height (see
  // renderStatsRow), which is what keeps every row below in line. Always
  // mounted and animated on height, so opening one row and closing another
  // move together rather than jumping.
  const renderStartersPanel = (vm) => {
    const { member, expanded } = vm;
    // The current week reads the member row. A past week reads that week's
    // weekly_lineups row off the live member record: weekly_standings rows,
    // which past weeks render, carry no lineup.
    const lineup = isCurrent
      ? { starters: member.lineup?.starters, captain: member.captain, tripPlayTeam: member.tripPlayTeam, teamPoints: member.teamPoints }
      : membersById.get(member.id)?.lineupsByWeek?.[selectedWeek] || {};
    const starters = Array.isArray(lineup.starters) ? lineup.starters : [];
    const slots = Array.from({ length: STARTER_SLOTS }, (_, i) => starters[i] || null);
    const teamPoints = lineup.teamPoints || {};

    return (
      <div
        aria-hidden={!expanded}
        style={{
          position: "relative",
          zIndex: 1,
          width: `calc(100% + ${STATS_VISIBLE_WIDTH + 1}px)`,
          height: expanded ? EXPANDED_HEIGHT : 0,
          overflow: "hidden",
          transition: "height 0.2s",
          backgroundColor: "var(--color-bg-row-alt)",
          borderTop: expanded ? "1px solid var(--color-border)" : "none",
          borderBottom: expanded ? "1px solid var(--color-border)" : "none",
        }}
      >
        <div className="grid grid-cols-5 gap-1" style={{ padding: "10px 8px" }}>
          {slots.map((slug, i) => {
            if (!slug) {
              return (
                <div key={i} className="flex flex-col items-center text-center min-w-0">
                  <div className="rounded-full" style={{ width: 32, height: 32, border: "1.5px dashed var(--color-border)" }} />
                  <span className="text-[11px] leading-tight mt-1" style={{ color: "var(--color-text-muted)" }}>Empty</span>
                </div>
              );
            }
            const key = normalizeTeamName(slug);
            const team = allTeams?.[key];
            const name = team?.school || teamLabel(slug, allTeams);
            const isCaptain = lineup.captain && normalizeTeamName(lineup.captain) === key;
            const isTripPlay = lineup.tripPlayTeam && normalizeTeamName(lineup.tripPlayTeam) === key;

            // Past weeks are over, so every game in them is final and no
            // games query is needed to say so.
            let status = { label: "Final", color: "var(--color-text-muted)", live: false };
            if (isCurrent) {
              const game = getScheduleEntry(scheduleData || {}, slug);
              if (!game) status = { label: "Bye", color: "var(--color-text-faint)", live: false };
              else if (game.gameComplete) status = { label: "Final", color: "var(--color-text-muted)", live: false };
              else if (game.hasLiveGame) status = { label: "Live", color: "var(--color-success)", live: true };
              else status = { label: "Not started", color: "var(--color-text-faint)", live: false };
            }
            // Earned points, multipliers applied, so a captain shows double.
            // Only once the game has started: before that the scorer has not
            // written this team, and a bye never gets an entry at all.
            const rawPts = teamPoints[slug] ?? teamPoints[key];
            const pts = (status.label === "Final" || status.live) && typeof rawPts === "number" ? rawPts : null;

            return (
              <div key={i} className="flex flex-col items-center text-center min-w-0">
                {/* Logo and name are one tap target for the team drawer. The
                    status stays outside it: it describes the game, not the
                    team. Out of the tab order while the panel is shut. */}
                <button
                  type="button"
                  onClick={() => setDrawerTeam(name)}
                  tabIndex={expanded ? 0 : -1}
                  className="flex flex-col items-center w-full min-w-0 rounded-lg active:opacity-60"
                  aria-label={`View ${name}`}
                >
                <div className="relative">
                  <TeamLogoImage teamName={name} primaryColor={team?.colors?.primary || team?.color} size={32} />
                  {(isCaptain || isTripPlay) && (
                    <span
                      className="absolute flex items-center justify-center rounded-full"
                      style={{ top: -3, right: -5, width: 16, height: 16, backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}
                      title={isTripPlay ? "Captain, 5x Play" : "Captain"}
                    >
                      {isTripPlay
                        ? <Zap size={10} style={{ color: "var(--color-tripplay-text)" }} />
                        : <Crown size={10} style={{ color: "var(--color-captain-deep)" }} />}
                    </span>
                  )}
                </div>
                <span
                  className="text-[11px] font-semibold leading-tight mt-1 w-full"
                  style={{ color: "var(--color-text-strong)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere", height: 27 }}
                >
                  {name}
                </span>
                </button>
                <span className="flex items-center gap-1 text-[10px] font-semibold mt-0.5 whitespace-nowrap" style={{ color: status.color }}>
                  {status.live && <span className="rounded-full animate-pulse" style={{ width: 6, height: 6, backgroundColor: "var(--color-success)" }} />}
                  {status.label}
                  {pts != null && (
                    <span style={{ color: pts < 0 ? "var(--color-danger-fill)" : "var(--color-text-strong)", fontWeight: 700 }}>
                      {pts}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Stats (Total, Wk, Best, Worst, Dec%, Capt%, Capt Pts, Bench, FA): the
  // scrolling right column.
  const renderStatsRow = (vm) => {
    const { member, decisionEntry, captainEntry, captainPtsEntry, benchPts, weekRangeEntry, faMoves, rowBg, rowHeight, showPlayoffLine, expanded } = vm;
    return (
      <div key={member.id}>
        {showPlayoffLine && renderPlayoffLine(false)}
        {/* Total leads, Wk follows: the season total is the standing, the
            week score is a supporting number, and ESPN's own standings order
            columns the same way (record before any one week's detail). */}
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{ backgroundColor: rowBg, height: rowHeight, overflow: "hidden", transition: "height 0.2s" }}
        >
          <div className="flex-shrink-0 text-center" style={{ width: TOTAL_COL_WIDTH }}>
            <span style={STAT_TEXT_STYLE}>{member.points ?? 0}</span>
          </div>
          <div className="flex-shrink-0 text-center" style={{ width: WK_COL_WIDTH }}>
            <span style={STAT_TEXT_STYLE}>{member.weeklyPoints ?? 0}</span>
          </div>
          {/* Best and Worst sit beside Wk: all three are one week's score. */}
          {[weekRangeEntry?.best, weekRangeEntry?.worst].map((entry, i) => (
            <div
              key={i}
              className="flex-shrink-0 text-center"
              style={{ width: STAT_COL_WIDTH }}
              title={entry ? `Week ${entry.week}` : "No finished week yet"}
            >
              <span style={entry ? STAT_TEXT_STYLE : { ...STAT_TEXT_STYLE, color: "var(--color-text-faint)" }}>
                {entry ? entry.value : "—"}
              </span>
            </div>
          ))}
          <div
            className="flex-shrink-0 text-center"
            style={{ width: STAT_COL_WIDTH }}
            title={decisionEntry ? `${decisionEntry.correct}/${decisionEntry.total} decisions` : "No fully-decided week yet"}
          >
            <span style={decisionEntry ? STAT_TEXT_STYLE : { ...STAT_TEXT_STYLE, color: "var(--color-text-faint)" }}>
              {decisionEntry ? formatRatio(decisionEntry.correct, decisionEntry.total) : "—"}
            </span>
          </div>
          <div
            className="flex-shrink-0 text-center"
            style={{ width: STAT_COL_WIDTH }}
            title={captainEntry ? `${captainEntry.correct}/${captainEntry.total} captain calls` : "No fully-decided week yet"}
          >
            <span style={captainEntry ? STAT_TEXT_STYLE : { ...STAT_TEXT_STYLE, color: "var(--color-text-faint)" }}>
              {captainEntry ? formatRatio(captainEntry.correct, captainEntry.total) : "—"}
            </span>
          </div>
          <div
            className="flex-shrink-0 text-center"
            style={{ width: CAPT_PTS_COL_WIDTH }}
            title={captainPtsEntry ? `${captainPtsEntry.value} captain points over ${captainPtsEntry.weeks} ${captainPtsEntry.weeks === 1 ? "week" : "weeks"}` : "No captain points yet"}
          >
            <span style={captainPtsEntry ? STAT_TEXT_STYLE : { ...STAT_TEXT_STYLE, color: "var(--color-text-faint)" }}>
              {captainPtsEntry ? captainPtsEntry.value : "—"}
            </span>
          </div>
          <div className="flex-shrink-0 text-center" style={{ width: STAT_COL_WIDTH }} title={`${benchPts} points left on the bench`}>
            <span style={STAT_TEXT_STYLE}>{benchPts}</span>
          </div>
          <div className="flex-shrink-0 text-center" style={{ width: STAT_COL_WIDTH }} title={`${faMoves} free agency / waiver move${faMoves === 1 ? "" : "s"}`}>
            <span style={STAT_TEXT_STYLE}>{faMoves}</span>
          </div>
        </div>
        {/* Holds the stats column open while the identity column's starters
            panel paints over it. Same height, same transition, or every row
            below drifts. */}
        <div style={{ height: expanded ? EXPANDED_HEIGHT : 0, transition: "height 0.2s" }} />
      </div>
    );
  };

  return (
    <div>
      {/* Full bleed below sm. See the note in LeagueTab: the gutter costs a
          phone more than it buys, so only the text blocks keep side padding
          there and the cards run to both edges. */}
      <div className="max-w-4xl mx-auto sm:px-6 pt-3 pb-28">
        {/* Header section: the week selector and nothing else. The dot, chip
            and stat-column legends that stacked up here cost three bands of
            a phone screen above the first manager, for reference a manager
            needs once. The "?" on the table header walks through the same
            marks on a real row instead (tourSteps.js, league-chips onward).
            No league name or week label here either: LeagueTab's title card
            carries both, and the selector's own pulse dot is the one live
            indicator. */}
        {/* Share leads the row at the same size and on the same side as on the
            Lineup tab's week row, so the control is in one place on both. It
            shares the table for whichever week the selector beside it is on.
            The row is always here for it: the selector alone used to decide
            whether there was a row at all, and has nothing to offer in week
            one. The negative margins let the 44px target overhang the row
            instead of setting its height. */}
        <div className="flex items-center gap-1 px-4 sm:px-1 pb-2">
          <div style={{ margin: "-8px 0 -8px -12px" }}>
            <ShareButton getText={getStandingsShareText} label="Share the standings" />
          </div>
          {(availableWeeks.length > 0 || currentWeekNum > 1) && (
            <div data-tour="league-weeks">
              <WeekSelector
                currentWeek={currentWeek}
                viewMode={viewMode}
                setViewMode={setViewMode}
                selectedWeek={selectedWeek}
                setSelectedWeek={setSelectedWeek}
                availableWeeks={availableWeeks}
                standingsLoading={standingsLoading}
                loadWeeklyStandings={loadWeeklyStandings}
                isLive={stage === "live"}
              />
            </div>
          )}
          {/* Over the stat columns it explains, and at the size of the share
              icon at the other end of the row: five of those seven columns
              are off screen until the table is swiped, so nothing on the
              table itself says they exist or that the headings sort. The
              small "?" by the table's title covers the marks on a row. */}
          {displayData.length > 0 && (
            <div className="ml-auto" style={{ margin: "-8px -2px -8px auto" }}>
              <HelpMark steps={["league-columns", "league-columns-more"]} label="What the stat columns mean" size={20} />
            </div>
          )}
        </div>

        {displayData.length === 0 ? (
          <div className="bg-surface-card sm:rounded-2xl p-10 border-y sm:border border-hairline shadow-sm text-center">
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
              {isCurrent
                ? "No managers have joined this league yet."
                : `There are no standings for week ${selectedWeek} yet.`}
            </p>
          </div>
        ) : (
          <div className="mb-5">
            {/* Two independent columns, not one scrolling row: identity
                (rank/avatar/name) is a plain block that never sits inside
                anything horizontally scrollable, so it genuinely cannot
                drift when the stats column scrolls. See the comment on
                renderIdentityRow for why position: sticky, tried first,
                wasn't reliable enough on iOS for this. Nothing inside either
                column needs overflow-hidden to avoid clipping a sticky
                element anymore either, so the rounded corners live on this
                outer wrapper again instead of being patched onto the first
                and last row. */}
            <div
              data-tour="league-standings"
              className="flex sm:rounded-2xl border-y sm:border shadow-sm overflow-hidden"
              style={{ position: "relative", backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
            >
              {/* The tour points at the head of the table and its top two
                  managers rather than the whole of it, which is taller than a
                  phone has room for beside it: see TourAnchor. */}
              <TourAnchor name="league-table" height={STANDINGS_TOUR_HEIGHT} />
              {/* Frozen: rank, avatar, name, chips. flex-grow, not a fixed
                  width: it takes whatever room STATS_VISIBLE_WIDTH doesn't
                  reserve, so a wider phone hands the extra space to the name
                  instead of leaving it unused past a hard-coded number.
                  Everything inside (this header label and every row) is
                  plain block content stacked in this one already-sized
                  column, not its own independently-computed flex item, so
                  nothing here can end up a different width than its
                  siblings the way the header/body split used to. */}
              <div
                className={namesCollapsed ? "flex-shrink-0" : "flex-1"}
                style={{ ...(namesCollapsed ? { width: IDENTITY_COLLAPSED_WIDTH } : { minWidth: IDENTITY_MIN_WIDTH }), borderRight: "1px solid var(--color-border)" }}
              >
                {/* flex + items-center, matching the stats header below and
                    every row underneath it, is load-bearing, not cosmetic: a
                    plain block wrapper here has no font-size class of its
                    own, so it inherits the page's default line-height and
                    lays down a full-size invisible "strut" behind the small
                    label text, several pixels taller than the same label
                    sitting in a flex container does. That extra height was
                    invisible on this row alone but every row below it is
                    stacked directly beneath it in a separate, independently-
                    scrolling column from the stats side, so the mismatch
                    pushed every single row out of alignment with its stats
                    counterpart by the difference, for the entire table. */}
                <div className="flex items-center gap-1 px-3 py-1.5">
                  <button
                    type="button"
                    onClick={() => toggleStatSort("rank")}
                    className="flex-shrink-0 flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: statSort.key === "rank" ? "var(--color-brand)" : "var(--color-text-muted)" }}
                    aria-label="Sort by rank"
                  >
                    Rk
                    {statSort.key === "rank" && (statSort.dir === "desc" ? <ChevronDown size={10} /> : <ChevronUp size={10} />)}
                  </button>
                  {!namesCollapsed && (
                    <>
                      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        League Standings
                      </span>
                      <HelpMark steps={["league-chips", "league-dots"]} label="What the marks on each row mean" size={13} />
                    </>
                  )}
                  {/* Folds this column down to rank and avatar and back. The
                      arrows point the way the column's edge will move. The
                      padding and matching negative margin give it a full
                      size target without making this row taller than the
                      stats header beside it. */}
                  <button
                    type="button"
                    onClick={() => setNamesCollapsed((v) => !v)}
                    aria-expanded={!namesCollapsed}
                    aria-label={namesCollapsed ? "Show manager names" : "Hide manager names to show more stats"}
                    className="flex-shrink-0"
                    style={{ margin: "-15px -12px -15px auto", padding: 15, color: "var(--color-brand)", lineHeight: 0 }}
                  >
                    {namesCollapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
                  </button>
                </div>
                {rowViewModels.map(renderIdentityRow)}
              </div>

              {/* Scrolling: Total, Wk, Dec%, Capt%, FA. Fixed at exactly
                  STATS_VISIBLE_WIDTH (not flex-1): that's what keeps Total
                  and Wk the only columns ever visible without scrolling, on
                  every phone regardless of how much screen width is
                  actually available, rather than a wide enough phone having
                  room to show Dec%/Capt%/FA for free. width: max-content +
                  minWidth: 100% on the inner wrapper is what makes the
                  scroll happen at all: a plain 100%-wide block never reports
                  itself as overflowing to the overflow-x-auto ancestor even
                  when its own flex children refuse to shrink, so the extra
                  columns would otherwise just get clipped instead of
                  becoming reachable by scroll. Every column here is
                  tap-to-sort now, Rank (identity side, above) included: with
                  Rank able to re-sort the table back to real standing order,
                  Total no longer has to be the one column that's off limits
                  to keep that order reachable. */}
              {/* The tour points at this fixed-width window, not at the header
                  row inside it: that row is wider than the window, and
                  lighting it up scrolled Total out of view to centre it. */}
              <div className={namesCollapsed ? "flex-1 min-w-0" : "flex-shrink-0"} style={{ position: "relative" }}>
              {/* The column headings and top two rows, as above. Inside the
                  fixed-width window, so lighting them up never scrolls the
                  columns sideways. */}
              <TourAnchor name="league-columns-more" height={STANDINGS_TOUR_HEIGHT} />
              <TourAnchor name="league-columns" height={STANDINGS_TOUR_HEIGHT} />
              <div className="overflow-x-auto" style={{ width: namesCollapsed ? "100%" : STATS_VISIBLE_WIDTH }}>
                <div style={{ width: "max-content", minWidth: "100%" }}>
                  <div className="flex items-center gap-2 px-3 py-1.5">
                    {[
                      { key: "total", label: "Total", width: TOTAL_COL_WIDTH },
                      { key: "week", label: "Wk", width: WK_COL_WIDTH },
                      { key: "bestWeek", label: "Best", width: STAT_COL_WIDTH },
                      { key: "worstWeek", label: "Worst", width: STAT_COL_WIDTH },
                      { key: "decision", label: "Dec%", width: STAT_COL_WIDTH },
                      { key: "captain", label: "Capt%", width: STAT_COL_WIDTH },
                      { key: "captainPts", label: "Capt Pts", width: CAPT_PTS_COL_WIDTH },
                      { key: "bench", label: "Bench", width: STAT_COL_WIDTH },
                      { key: "fa", label: "FA", width: STAT_COL_WIDTH },
                    ].map(({ key, label, width }) => {
                      const active = statSort.key === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => toggleStatSort(key)}
                          // nowrap is the guarantee, the widths are only
                          // the fit: whatever font a phone falls back to, a
                          // heading may overhang its column but can never
                          // become two lines and change this row's height.
                          className="flex-shrink-0 flex items-center justify-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap"
                          style={{ width, color: active ? "var(--color-brand)" : "var(--color-text-muted)" }}
                          aria-label={`Sort by ${label}`}
                        >
                          {label}
                          {active && (statSort.dir === "desc" ? <ChevronDown size={10} className="flex-shrink-0" /> : <ChevronUp size={10} className="flex-shrink-0" />)}
                        </button>
                      );
                    })}
                  </div>
                  {rowViewModels.map(renderStatsRow)}
                </div>
              </div>
              </div>
            </div>
          </div>
        )}

        {isCurrent && (
          <div className="text-center mb-6">
            <button
              onClick={() => setShowScoringModal(true)}
              className="text-brand hover:text-brand text-sm font-medium underline underline-offset-2"
            >
              How does scoring work?
            </button>
          </div>
        )}

        {/* Directly below the table rather than a tap away: it was a doorway
            button before, and a manager scrolling past their own standing is
            exactly the moment this is worth seeing. Independent of the week
            selector above: the record book is season-wide, not tied to
            whichever week the table happens to be showing. */}
        <HallOfFame embedded />

        <TeamDrawer teamName={drawerTeam} onClose={() => setDrawerTeam(null)} onSelectTeam={setDrawerTeam} />

      </div>

      {showScoringModal && <ScoringSystemModal onClose={() => setShowScoringModal(false)} />}

      {zoomedMember && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
          onClick={() => setZoomedMember(null)}
          role="presentation"
        >
          <div
            className="bg-surface-card rounded-2xl shadow-xl px-6 pt-6 pb-5 flex flex-col items-center"
            style={{ maxWidth: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <UserAvatar member={zoomedMember} size={200} />
            <div className="text-lg font-bold mt-4 text-center" style={{ color: "var(--color-text-strong)" }}>
              {zoomedMember.teamName || "Unnamed Team"}
            </div>
            <div className="text-sm text-center" style={{ color: "var(--color-text-muted)" }}>
              {zoomedMember.firstName || "Unknown"}
            </div>
            {/* Only on other managers: reporting or blocking yourself is not
                a thing, and the row is the only place another manager's
                name, avatar and smack talk all appear together. */}
            {zoomedMember.id !== currentMemberId && (
              <button
                type="button"
                onClick={() => {
                  const target = zoomedMember;
                  setZoomedMember(null);
                  setReportTarget(target);
                }}
                className="mt-4 text-sm font-medium"
                style={{ color: "var(--color-text-muted)" }}
              >
                Report or block
              </button>
            )}
            <button
              type="button"
              onClick={() => setZoomedMember(null)}
              className="mt-3 text-sm font-medium text-brand"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {reportTarget && (
        <ReportBlockModal
          member={reportTarget}
          leagueId={leagueId}
          isBlocked={!!blockedIds?.has(reportTarget.userId || reportTarget.user_id)}
          onClose={() => setReportTarget(null)}
          onChanged={() => { setReportTarget(null); refreshLeague?.(); }}
        />
      )}

      {smackTalkBubble && (
        <div
          data-smack-talk-bubble
          onClick={() => setSmackTalkBubble(null)}
          className="rounded-xl shadow-lg px-3 py-2"
          style={{
            position: "fixed",
            zIndex: 50,
            // Flips above the button when there is not room below it, so a
            // manager near the bottom of a twelve-team table can still read
            // the whole line instead of the first line of it.
            ...(smackTalkBubble.flipAbove
              ? { bottom: window.innerHeight - smackTalkBubble.rect.top + 6 }
              : { top: smackTalkBubble.rect.bottom + 6 }),
            // Anchored to the button's right edge, since the button itself
            // sits at the right edge of the row. Clamped to the viewport at
            // both ends so it can never hang off either side.
            left: Math.max(
              8,
              Math.min(
                smackTalkBubble.rect.right - BUBBLE_WIDTH,
                window.innerWidth - BUBBLE_WIDTH - 8
              )
            ),
            width: BUBBLE_WIDTH,
            backgroundColor: "var(--color-text-strong)",
            color: "var(--color-bg-card)",
          }}
        >
          {/* overflowWrap anywhere is the fix for the right edge overlapping:
              smack talk is up to 80 characters of whatever someone types, and
              an unbroken run of them offers the browser nowhere to break. Word
              wrapping alone leaves it running straight out of the bubble. */}
          <span
            className="text-[13px] block"
            style={{ overflowWrap: "anywhere", lineHeight: 1.35 }}
          >
            {smackTalkBubble.text}
          </span>
        </div>
      )}
    </div>
  );
}

export default MyLeague;
